import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientLandingRunningCount } from '../../../../store/crossClient/crossClientLanding'
import { type PredefinedCrossClientQuery } from '../../../../models/CrossClient'
import { useRecommendedCrossClientQueries } from '../../Predefined/useRecommendedCrossClientQueries'
import { CrossClientLandingPredefinedQueries } from '../Predefined/CrossClientLandingPredefinedQueries'
import { CrossClientLandingScheduled } from '../Scheduled/CrossClientLandingScheduled'
import { useCrossClientScheduled } from '../Scheduled/useCrossClientScheduled'
import { CrossClientNeedsAction } from '../NeedsAction/CrossClientNeedsAction'
import { useCrossClientNeedsActionGroups } from '../NeedsAction/useCrossClientNeedsActionGroups'
import { CrossClientCompleted } from '../Completed/CrossClientCompleted'
import { useCrossClientCompleted } from '../Completed/useCrossClientCompleted'
import { CrossClientRunning } from '../Running/CrossClientRunning'
import { CrossClientOwnerFilter } from '../OwnerFilter/CrossClientOwnerFilter'
import { CrossClientRunningExecutionsPoller } from '../RunningExecutions/CrossClientRunningExecutionsPoller'
import { CrossClientListPoller } from '../RunningExecutions/CrossClientListPoller'
import { CrossClientTabBar, type CrossClientTab, type CrossClientTabItem } from './CrossClientTabBar'
import './CrossClientLandingTabs.scss'

const CROSS_CLIENT_TABS: CrossClientTab[] = ['needs-action', 'running', 'completed', 'recommended', 'scheduled']
const CROSS_CLIENT_TAB_PARAM = 'tab'

export interface CrossClientLandingTabsProps {
  isAdmin: boolean
  runDisabled: boolean
  onPredefinedQuerySelect: (predefinedQuery: PredefinedCrossClientQuery) => void
}

export const CrossClientLandingTabs: React.FC<CrossClientLandingTabsProps> = ({ isAdmin, runDisabled, onPredefinedQuerySelect }) => {
  // Persist the active tab in the URL (?tab=…) so returning to the landing restores the previous tab.
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get(CROSS_CLIENT_TAB_PARAM) as CrossClientTab | null
  const activeTab: CrossClientTab = tabParam && CROSS_CLIENT_TABS.includes(tabParam) ? tabParam : 'needs-action'
  const setActiveTab = (tab: CrossClientTab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set(CROSS_CLIENT_TAB_PARAM, tab)
        return next
      },
      { replace: true },
    )
  }

  const runningCount = useAppSelector(selectCrossClientLandingRunningCount)

  const needsActionGroups = useCrossClientNeedsActionGroups()
  const completed = useCrossClientCompleted()
  const scheduled = useCrossClientScheduled()
  const recommended = useRecommendedCrossClientQueries({ skip: activeTab !== 'recommended' })

  const tabs: CrossClientTabItem[] = [
    { key: 'needs-action', label: 'Needs action', count: needsActionGroups.totalCount },
    { key: 'running', label: 'Running', count: runningCount },
    { key: 'completed', label: 'Completed', count: completed.totalCount },
    { key: 'recommended', label: 'Recommended', count: recommended.queries.length, hideCount: true },
    { key: 'scheduled', label: 'Scheduled', count: scheduled.totalCount },
  ]

  return (
    <div className='d-flex flex-column zocks-gap-1 w-100'>
      <CrossClientRunningExecutionsPoller />
      <CrossClientListPoller />
      <CrossClientTabBar
        tabs={tabs}
        activeTab={activeTab}
        onSelect={setActiveTab}
        rightSlot={isAdmin && activeTab !== 'recommended' ? <CrossClientOwnerFilter /> : null}
      />
      <div
        role='tabpanel'
        id='cross-client-tabpanel'
        aria-labelledby={`cross-client-tab-${activeTab}`}
        className='d-flex flex-column gap-2 w-100 cross-client-tabs-panel'
      >
        <div key={activeTab} className='d-flex flex-column gap-2 w-100 cross-client-tabs-panel-content'>
          {activeTab === 'needs-action' ? <CrossClientNeedsAction groups={needsActionGroups} /> : null}
          {activeTab === 'completed' ? <CrossClientCompleted completed={completed} /> : null}
          {activeTab === 'running' ? <CrossClientRunning /> : null}
          {activeTab === 'recommended' ? (
            <CrossClientLandingPredefinedQueries
              queries={recommended.queries}
              loading={recommended.loading}
              isError={recommended.isError}
              onSelect={onPredefinedQuerySelect}
              runDisabled={runDisabled}
            />
          ) : null}
          {activeTab === 'scheduled' ? <CrossClientLandingScheduled scheduled={scheduled} /> : null}
        </div>
      </div>
    </div>
  )
}
